import type { UsageProviderKind } from "@cmmd-forge/contracts";
import { useRef, useState } from "react";

import type { DailyTotals, HourlyTotals } from "@cmmd-forge/shared/usageMerge";
import {
  formatDayShort,
  formatHourShort,
  formatRelativeHourShort,
  formatTokens,
  formatUsd,
} from "@cmmd-forge/shared/usageFormat";

import {
  buildPeriodColumns,
  curvePath,
  niceScale,
  smoothCurve,
  type DayColumn,
  type UsageChartMetric,
} from "./usageChartGeometry";
import { UsageChartTooltip, useTooltipPlacement } from "./UsageChartTooltip";
import { PROVIDER_ORDER, PROVIDER_PRESENTATION } from "./usageProviders";

export type { UsageChartMetric } from "./usageChartGeometry";

const VIEW_WIDTH = 960;
const VIEW_HEIGHT = 260;
const TICK_COUNT = 4;
const PLOT_TOP = 8;

interface UsageProviderChartProps {
  /** Lanes with activity, in reading order. Also the tooltip's row order. */
  readonly providers: readonly UsageProviderKind[];
  readonly days: readonly string[];
  readonly daily: readonly DailyTotals[];
  readonly hours: readonly string[];
  readonly hourly: readonly HourlyTotals[];
  readonly metric: UsageChartMetric;
  readonly referenceTime: string | undefined;
  readonly resolution: "day" | "hour";
  readonly timeZone: string;
}

interface ChartGeometry {
  readonly paths: readonly { provider: UsageProviderKind; area: string; line: string }[];
  readonly ticks: readonly number[];
  readonly stepX: number;
  readonly toY: (value: number) => number;
  readonly series: readonly DayColumn[];
}

function buildGeometry(
  periods: readonly string[],
  byPeriod: ReadonlyMap<string, DailyTotals | HourlyTotals>,
  metric: UsageChartMetric,
  providers: readonly UsageProviderKind[],
): ChartGeometry {
  if (periods.length === 0) {
    return { paths: [], ticks: [0], stepX: 0, toY: () => VIEW_HEIGHT, series: [] };
  }

  const columns = buildPeriodColumns(periods, byPeriod, metric);

  // The scale tops out at the largest single provider-period, not the largest
  // sum: layered series each measure from zero, so a combined peak would leave
  // the plot permanently half empty.
  const peak = columns.reduce(
    (max, column) => column.bands.reduce((inner, band) => Math.max(inner, band.value), max),
    0,
  );
  const { max, ticks } = niceScale(peak, TICK_COUNT);
  const stepX = periods.length === 1 ? 0 : VIEW_WIDTH / (periods.length - 1);
  // Reserve a sliver above the top gridline so the series stroke, drawn at
  // constant screen width, is not shaved off at a peak.
  const toY = (value: number) =>
    max === 0 ? VIEW_HEIGHT : VIEW_HEIGHT - (value / max) * (VIEW_HEIGHT - PLOT_TOP);

  // Only lanes with activity get a series: a flat zero line reads as a real
  // measurement of nothing rather than as an absence of data.
  const built = providers.map((provider) => {
    const providerIndex = PROVIDER_ORDER.indexOf(provider);
    const curve = smoothCurve(
      columns.map((column, periodIndex) => ({
        x: periodIndex * stepX,
        y: toY(column.bands[providerIndex]?.value ?? 0),
      })),
    );
    const line = curvePath(curve, "M");
    return {
      provider,
      total: columns.reduce((sum, column) => sum + (column.bands[providerIndex]?.value ?? 0), 0),
      area: line === "" ? "" : `${line} L${VIEW_WIDTH},${VIEW_HEIGHT} L0,${VIEW_HEIGHT} Z`,
      line,
    };
  });

  // Paint the heavier series first so a lighter one is never buried under it.
  // The strokes are drawn in a second pass regardless, so neither can be hidden.
  return {
    paths: built.toSorted((a, b) => b.total - a.total),
    ticks,
    stepX,
    toY,
    series: columns,
  };
}

/** Totals keyed by the period string the axis is drawn from. */
function indexPeriods(
  resolution: "day" | "hour",
  daily: readonly DailyTotals[],
  hourly: readonly HourlyTotals[],
): Map<string, DailyTotals | HourlyTotals> {
  const entries: readonly (readonly [string, DailyTotals | HourlyTotals])[] =
    resolution === "hour"
      ? hourly.map((entry) => [entry.hourStart, entry] as const)
      : daily.map((entry) => [entry.day, entry] as const);
  return new Map(entries);
}

/**
 * Axis and tooltip labels for one resolution.
 *
 * The tooltip prefers a relative hour ("3h ago") because the reader is looking
 * at one point in a 24-hour window; the axis stays absolute.
 */
function makePeriodFormatters(
  resolution: "day" | "hour",
  timeZone: string,
  referenceTime: string | undefined,
): {
  readonly formatPeriod: (period: string) => string;
  readonly formatTooltipPeriod: (period: string) => string;
} {
  const formatPeriod = (period: string) =>
    resolution === "hour" ? formatHourShort(period, timeZone) : formatDayShort(period);
  const formatTooltipPeriod = (period: string) =>
    resolution === "hour" && referenceTime !== undefined
      ? formatRelativeHourShort(period, referenceTime, timeZone)
      : formatPeriod(period);
  return { formatPeriod, formatTooltipPeriod };
}

/** Nearest period index under the pointer, or `null` when there is nothing to hit. */
function hoverIndexAt(clientX: number, bounds: DOMRect | undefined, count: number): number | null {
  if (bounds === undefined || bounds.width === 0 || count === 0) return null;
  const fraction = (clientX - bounds.left) / bounds.width;
  const index = Math.round(fraction * (count - 1));
  return Math.min(count - 1, Math.max(0, index));
}

function chartAriaLabel(resolution: "day" | "hour", metric: UsageChartMetric): string {
  const cadence = resolution === "hour" ? "Hourly" : "Daily";
  const measure = metric === "tokens" ? "processed tokens" : "cost";
  return `${cadence} ${measure} by provider`;
}

export function UsageProviderChart({
  providers,
  days,
  daily,
  hours,
  hourly,
  metric,
  referenceTime,
  resolution,
  timeZone,
}: UsageProviderChartProps) {
  const periods = resolution === "hour" ? hours : days;
  const byPeriod = indexPeriods(resolution, daily, hourly);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const plotRef = useRef<HTMLDivElement | null>(null);
  const { tooltipRef, trackPointer, clearPointer } = useTooltipPlacement(plotRef, hoverIndex);

  const { paths, ticks, stepX, toY, series } = buildGeometry(periods, byPeriod, metric, providers);
  const format = metric === "tokens" ? formatTokens : formatUsd;
  const { formatPeriod, formatTooltipPeriod } = makePeriodFormatters(
    resolution,
    timeZone,
    referenceTime,
  );

  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = plotRef.current?.getBoundingClientRect();
    trackPointer(event.clientX, event.clientY, bounds);
    setHoverIndex(hoverIndexAt(event.clientX, bounds, periods.length));
  };

  const hoveredPeriod = hoverIndex === null ? undefined : periods[hoverIndex];
  const hoveredColumn = hoverIndex === null ? undefined : series[hoverIndex];

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2">
        {/* Axis labels sit outside the plot so they stay aligned to gridlines. */}
        <div className="relative h-56 w-14 shrink-0">
          {ticks.map((tick) => (
            <span
              key={tick}
              className="absolute right-0 -translate-y-1/2 text-[10px] text-muted-foreground tabular-nums"
              style={{ top: `${(toY(tick) / VIEW_HEIGHT) * 100}%` }}
            >
              {tick === 0 ? "0" : format(tick)}
            </span>
          ))}
        </div>

        <div
          ref={plotRef}
          className="relative h-56 flex-1"
          onMouseMove={handleMove}
          onMouseLeave={() => {
            clearPointer();
            setHoverIndex(null);
          }}
        >
          <svg
            className="h-full w-full"
            viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={chartAriaLabel(resolution, metric)}
          >
            {ticks.map((tick) => (
              <line
                key={tick}
                x1={0}
                x2={VIEW_WIDTH}
                y1={toY(tick)}
                y2={toY(tick)}
                stroke="currentColor"
                strokeWidth={1}
                className="text-border"
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {/* Fills first, then every stroke, so no series covers another's line. */}
            {paths.map(({ provider, area }) => (
              <path
                key={provider}
                d={area}
                fill={PROVIDER_PRESENTATION[provider].color}
                fillOpacity={0.12}
              />
            ))}
            {paths.map(({ provider, line }) => (
              <path
                key={provider}
                d={line}
                fill="none"
                stroke={PROVIDER_PRESENTATION[provider].color}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {hoverIndex === null ? null : (
              <line
                x1={hoverIndex * stepX}
                x2={hoverIndex * stepX}
                y1={PLOT_TOP}
                y2={VIEW_HEIGHT}
                stroke="currentColor"
                strokeWidth={1}
                className="text-muted-foreground"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>

          {hoveredPeriod === undefined ? null : (
            <UsageChartTooltip
              ref={tooltipRef}
              column={hoveredColumn}
              providers={providers}
              periodLabel={formatTooltipPeriod(hoveredPeriod)}
              format={format}
            />
          )}
        </div>
      </div>

      <UsageChartAxisLabels periods={periods} formatPeriod={formatPeriod} />
    </div>
  );
}

interface UsageChartAxisLabelsProps {
  readonly periods: readonly string[];
  readonly formatPeriod: (period: string) => string;
}

/** First, middle, and last period under the plot. */
function UsageChartAxisLabels({ periods, formatPeriod }: UsageChartAxisLabelsProps) {
  const label = (index: number) => {
    const period = periods[index];
    return period === undefined ? "" : formatPeriod(period);
  };
  return (
    <div className="flex justify-between pl-16 text-[10px] text-muted-foreground uppercase">
      <span>{label(0)}</span>
      <span>{label(Math.floor(periods.length / 2))}</span>
      <span>{label(periods.length - 1)}</span>
    </div>
  );
}
